import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import type { PublicUserDocument, UserDocument } from 'models/user';
import { SupportState, type ChatDocument } from 'models/chat';
import {
  flagChat,
  hideChat,
  markAsUnread,
  removeCaseNumber,
  resolveChat,
  updateChat,
} from '@util/firestore/messages';
import { getOrderByOrderNum } from '@util/firestore/orders';
import { logError } from '@util/logError';

export interface SupportAction {
  id: string;
  label: string;
  category: 'admin' | 'chat' | 'case';
  onClick: () => void | Promise<void>;
  condition: (props: SupportActionContext) => boolean;
  loading?: boolean;
  href?: string;
  target?: string;
  type?: 'primary' | 'secondary' | 'tertiary' | 'danger';
}

export interface SupportActionContext {
  userDoc: UserDocument | null | undefined;
  recipientDoc: PublicUserDocument | null | undefined;
  chat: ChatDocument;
  setChat: (chat: ChatDocument) => void;
  isAdmin: boolean;
  isSupport: boolean;
}

export const useSupportChatActions = () => {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const queryClient = useQueryClient();
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>(
    {}
  );

  const isSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const isAdmin = userDoc?.roles?.admin;

  const setActionLoading = (actionId: string, loading: boolean) => {
    setLoadingActions((prev) => ({ ...prev, [actionId]: loading }));
  };

  const createActions = (
    actionContext: SupportActionContext
  ): SupportAction[] => {
    const { chat, setChat, recipientDoc } = actionContext;

    return [
      // Admin Actions
      {
        id: 'copy-uid',
        label: 'Copy UID',
        category: 'admin',
        type: 'tertiary',
        condition: ({ isAdmin }) => isAdmin,
        onClick: () => {
          navigator.clipboard.writeText(recipientDoc?.uid ?? '');
          showSuccessToast('Copied to clipboard');
        },
      },
      {
        id: 'open-backend',
        label: 'Open in Backend',
        category: 'admin',
        type: 'tertiary',
        condition: ({ isAdmin }) => isAdmin,
        href: `https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fmessages~2F${chat.id}`,
        target: '_blank',
        onClick: () => {},
      },

      // Chat Actions
      {
        id: 'flag-chat',
        label: chat.flagged ? 'Unflag Chat' : 'Flag Chat',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          try {
            setActionLoading('flag-chat', true);
            await flagChat(chat.id!, !chat.flagged);

            queryClient.invalidateQueries({
              queryKey: ['adminFlaggedChats'],
            });

            setChat({ ...chat, flagged: !chat.flagged });
            showSuccessToast('Chat flagged');
          } catch (error) {
            showErrorToast('Error flagging chat');
          } finally {
            setActionLoading('flag-chat', false);
          }
        },
      },
      {
        id: 'mark-unread',
        label: 'Mark as Unread',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) =>
          isSupport && !!process.env.NEXT_PUBLIC_SUPPORT_ID,
        loading: loadingActions['mark-unread'],
        onClick: async () => {
          if (!process.env.NEXT_PUBLIC_SUPPORT_ID) return;
          try {
            setActionLoading('mark-unread', true);
            await markAsUnread(chat.id, process.env.NEXT_PUBLIC_SUPPORT_ID);
            showSuccessToast('Marked as unread');
          } catch (e) {
            showErrorToast('Error marking as unread');
          } finally {
            setActionLoading('mark-unread', false);
          }
        },
      },
      {
        id: 'share-chat',
        label: 'Share Chat Link',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: () => {
          navigator.clipboard
            .writeText(
              `${window.location.origin}/dashboard/messages/${chat.id}`
            )
            .then(() => {
              showSuccessToast('Copied to clipboard');
            });
        },
      },

      // Case Management Actions
      {
        id: 'resolve-case',
        label: 'Resolve Case',
        category: 'case',
        type: 'secondary',
        condition: ({ isSupport, chat }) =>
          isSupport &&
          !!chat.case_num &&
          ![SupportState.Resolved, SupportState.Cancelled].includes(
            chat.support_state ?? 0
          ),
        loading: loadingActions['resolve-case'],
        onClick: async () => {
          setActionLoading('resolve-case', true);
          try {
            await resolveChat(chat.id!, userDoc?.uid!);
            showSuccessToast('Resolved');
            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });
          } catch (e) {
            showErrorToast((e as Error).message);
          } finally {
            setActionLoading('resolve-case', false);
          }
        },
      },
      {
        id: 'remove-case-number',
        label: 'Remove Case #',
        category: 'case',
        type: 'secondary',
        condition: ({ isAdmin }) => isAdmin,
        loading: loadingActions['remove-case-number'],
        onClick: async () => {
          setActionLoading('remove-case-number', true);
          try {
            await removeCaseNumber(chat.id!, userDoc?.uid!);
            showSuccessToast('Case number removed');
            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['allUnread'],
            });
          } catch (e) {
            showErrorToast((e as Error).message);
          } finally {
            setActionLoading('remove-case-number', false);
          }
        },
      },
      {
        id: 'hide-chat',
        label: 'Hide Chat',
        category: 'case',
        type: 'secondary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          try {
            setActionLoading('hide-chat', true);
            await hideChat(chat.id!, userDoc?.uid ?? '');

            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });

            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });

            showSuccessToast('Chat hidden');
          } catch (error) {
            showErrorToast('Error hiding chat');
          } finally {
            setActionLoading('hide-chat', false);
          }
        },
      },
      {
        id: 'link-order',
        label: 'Link Order',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          const order_num = prompt(
            'Enter the order number to link this chat to: '
          );
          if (!order_num) return;
          if (isNaN(+order_num)) {
            showErrorToast('Invalid order number');
            return;
          }
          try {
            setActionLoading('link-order', true);
            const order = await getOrderByOrderNum(+order_num);
            if (!order) {
              showErrorToast('Order not found');
              return;
            }
            await updateChat(chat.id!, { order_id: order.id });
            showSuccessToast('Order linked');
          } catch (error) {
            logError(
              error,
              undefined,
              undefined,
              'useSupportChatActions link-order'
            );
            showErrorToast('Error linking order');
          } finally {
            setActionLoading('hide-chat', false);
          }
        },
      },
    ];
  };

  const getActions = (actionContext: SupportActionContext) => {
    const allActions = createActions(actionContext);
    return allActions.filter((action) => action.condition(actionContext));
  };

  const getActionsByCategory = (actionContext: SupportActionContext) => {
    const actions = getActions(actionContext);
    return {
      admin: actions.filter((action) => action.category === 'admin'),
      chat: actions.filter((action) => action.category === 'chat'),
      case: actions.filter((action) => action.category === 'case'),
    };
  };

  return {
    isSupport,
    isAdmin,
    getActions,
    getActionsByCategory,
    loadingActions,
  };
};
